import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ElementType,
  type ReactElement,
} from 'react';

import { BaseChip } from './BaseChip.js';
import * as styles from './GroupedChip.sty.js';
import type { ChipProps } from './types/chip.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useChipGroupContext } from '../chip-group/hooks/useChipGroupContext.js';
import { useInternalChipGroupContext } from '../chip-group/hooks/useInternalChipGroupContext.js';
import { ChipGroupActions } from '../chip-group/state/chip-group-state-reducer.js';

/**
 * Grouped chip component
 * @internal
 */
export const GroupedChip: <E extends ElementType = 'div'>(
  props: ChipProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: ChipProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      size: propSize,
      disabled: propDisabled,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const uniqueId = useId();

    const chipRef = useRef<HTMLElement | null>(null);
    const { width } = useContainerSize(chipRef);

    const rootRef = useMergeRefs<HTMLElement | null>([chipRef, forwardedRef]);
    const { hiddenKeys, visibleKeys } = useChipGroupContext();
    const internalChipGroupContext = useInternalChipGroupContext();
    const {
      disabled: chipGroupDisabled,
      size: chipGroupSize,
      dispatch,
    } = internalChipGroupContext || {};

    const disabled = propDisabled ?? chipGroupDisabled ?? false;
    const size = propSize ?? chipGroupSize ?? 'default';

    useEffect(() => {
      dispatch?.({
        type: ChipGroupActions.ADDCHIP,
        payload: { uniqueId, width },
      });

      return () => {
        dispatch?.({
          type: ChipGroupActions.REMOVECHIP,
          payload: {
            uniqueId,
          },
        });
      };
      // only for mount and unmount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      dispatch?.({
        type: ChipGroupActions.UPDATECHIP,
        payload: { uniqueId, width: width === 0 ? undefined : width },
      });
    }, [dispatch, uniqueId, width]);

    return (
      <div
        ref={rootRef}
        className={clsx('chip', styles.grouped, {
          [styles.first]:
            visibleKeys.includes(uniqueId) && visibleKeys.length === 1,
          [styles.hidden]: hiddenKeys.includes(uniqueId),
        })}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <BaseChip
          {...(remainingProps as ChipProps<E>)}
          disabled={disabled}
          size={size}
        >
          {children}
        </BaseChip>
      </div>
    );
  },
);
